import React, { useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import ScholarshipResultBanner from "./components/ScholarshipResultBanner";
import CollegeMentorAchievers from "./components/CollegeMentorAchievers";
import ScholarshipAdmissions from "./components/ScholarshipAdmissions";
import HandHoldingMentorship from "./components/HandHoldingMentorship";
import TopCollegesUniversities from "./components/TopCollegesUniversities";
import ExploreYourFuture from "./components/ExploreYourFuture";
import AdmissionsOpen from "./components/AdmissionsOpen";
import ArticlesBlogs from "./components/ArticlesBlogs";
import KnowledgeHubFAQ from "./components/KnowledgeHubFAQ";
import config from "../../util/config";
import logo from "../../images/navbar/College Mentor_Final_Logo-01.png";
import NewScholarshipResultBanner from "./components/NewScholarshipResultBanner";

const ScholarshipResults = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // Create title and descriptions as strings first
  const pageTitle = `College Mentor Scholarship ${currentYear} Results | Check Now`;
  const pageDescription = `Check College Mentor Scholarship ${currentYear} results online. View your rank, score, and selection status. See if you qualified for the ₹1 crore scholarship pool.`;
  const pageKeywords = `College Mentor Scholarship results, scholarship result ${currentYear}, merit list, scholarship selection list, College Mentor result, scholarship exam result, College Mentor Scholarship Test ${currentYear}, college mentor scholarship test results ${currentYear}, college mentor, scholarships, exams, talent reward exam, scholarship test results`;
  const ogTitle = `College Mentor Scholarship ${currentYear} Results | Check Your Rank & Score`;
  const ogDescription = `Check your College Mentor Scholarship ${currentYear} results, rank, score, and selection status. ₹1 crore* scholarship pool results now live.`;
  const twitterTitle = `College Mentor Scholarship ${currentYear} Results`;
  const twitterDescription = `View your College Mentor Scholarship ${currentYear} results, score, rank & merit list. Total scholarship pool worth ₹1 crore.`;
  const ogImageAlt = `College Mentor Scholarship ${currentYear} Results`;
  const canonicalUrl = `${config.apiUrl}/college-mentor-scholarship/results`;

  // Check if the device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkIfMobile);
    };
  }, []);

  // Add useEffect to directly set the document title and meta description
  useEffect(() => {
    // Set document title
    document.title = pageTitle;

    // Set meta description by directly manipulating the DOM
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", pageDescription);

    // Set meta keywords
    document
      .querySelector('meta[name="keywords"]')
      ?.setAttribute("content", pageKeywords);
  }, [pageTitle, pageDescription, pageKeywords]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          {/* Google Tag Manager Script */}
          <script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${config.GA_TRACKING_ID}`}
          ></script>
          <script>
            {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${config.GA_TRACKING_ID}');
          `}
          </script>

          {/* Meta Tags */}
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta name="keywords" content={pageKeywords} />
          <link rel="canonical" href={canonicalUrl} />

          {/* Open Graph Tags */}
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="College Mentor" />
          <meta
            property="og:image"
            content={`${config.apiUrl}/assets/images/scholarship-results-og.jpg`}
          />
          <meta property="og:image:alt" content={ogImageAlt} />

          {/* Twitter Card Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={twitterTitle} />
          <meta name="twitter:description" content={twitterDescription} />
          <meta
            name="twitter:image"
            content={`${config.apiUrl}/assets/images/scholarship-results-og.jpg`}
          />
          <meta name="robots" content="index, follow" />

          {/* Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Organization",
                  name: "College Mentor",
                  url: config.apiUrl,
                  sameAs: [
                    "https://www.facebook.com/people/My-College-Mentor/61570539353128/",
                    "https://x.com/college_mentor_",
                    "https://www.instagram.com/college.mentor_insta",
                    "https://www.linkedin.com/company/mycollegementor",
                    "https://www.youtube.com/@college.mentor",
                  ],
                },
                {
                  "@type": "WebPage",
                  "@id": `${config.apiUrl}/college-mentor-scholarship/results`,
                  url: `${config.apiUrl}/college-mentor-scholarship/results`,
                  name: `College Mentor Scholarship ${currentYear} Results`,
                  description: `Check your College Mentor Scholarship ${currentYear} results online. View score, rank, selection status, and download the merit list. Scholarship worth ₹1 crore distributed.`,
                  inLanguage: "en",
                  isPartOf: {
                    "@type": "WebSite",
                    "@id": config.apiUrl,
                  },
                  datePublished: `${currentYear}-04-15`,
                  dateModified: `${currentYear}-04-15`,
                },
                {
                  "@type": "EducationalOccupationalProgram",
                  name: `College Mentor Scholarship ${currentYear}`,
                  alternateName: `College Mentor Scholarship Exam ${currentYear}`,
                  educationalProgramMode: "online",
                  programType: "ScholarshipProgram",
                  description: `College Mentor conducted a national-level scholarship exam in ${currentYear} to identify and support meritorious students with a ₹1 crore scholarship pool. Results are now available online.`,
                  provider: {
                    "@type": "Organization",
                    name: "College Mentor",
                    url: config.apiUrl,
                  },
                  url: `${config.apiUrl}/college-mentor-scholarship/results`,
                  startTime: `${currentYear}-04-15T05:30:00Z`,
                },
                {
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: "Home",
                      item: config.apiUrl,
                    },
                    {
                      "@type": "ListItem",
                      position: 2,
                      name: "Scholarship",
                      item: `${config.apiUrl}/college-mentor-scholarship`,
                    },
                    {
                      "@type": "ListItem",
                      position: 3,
                      name: "Results",
                      item: `${config.apiUrl}/college-mentor-scholarship/results`,
                    },
                  ],
                },
              ],
            })}
          </script>
        </Helmet>
      </HelmetProvider>

      <div>
        {/* Use the new banner component for desktop and the original for mobile */}
        <NewScholarshipResultBanner />
        <CollegeMentorAchievers />
        <ScholarshipAdmissions />
        <HandHoldingMentorship />
        <TopCollegesUniversities />
        <ExploreYourFuture />
        <AdmissionsOpen />
        <ArticlesBlogs />
        <KnowledgeHubFAQ />
      </div>
    </>
  );
};

export default ScholarshipResults;
